import { useSlideEditorContext } from "@/components/slides/editor/SlideEditorContext";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import type { PhonicsEncodeSlideData } from "@ai-teach/types";
import { IconButton } from "@ai-teach/ui-web";
import { useEffect, useRef, useState } from "react";
import { FaPause, FaPlay } from "react-icons/fa";
import { HiX } from "react-icons/hi";
import { EditorContainer } from "../../EditorContainer";

const EMPTY_AUDIO = { src: null, id: null, filename: null };

function buildDefaultEncodeData(): PhonicsEncodeSlideData {
  return {
    audio: EMPTY_AUDIO,
    phoneme: null,
    questionMode: "multiple_choice",
    correctAnswer: null,
    options: [],
  };
}

export function PhonicsEncodeEditor() {
  const dispatch = useAppDispatch();
  const { slide } = useAppSelector((state) => state.slideEditor.present);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const { setActivePanel } = useSlideEditorContext();
  const slideData = (slide?.slideData ?? buildDefaultEncodeData()) as PhonicsEncodeSlideData;
  const { audio, phoneme, questionMode, correctAnswer, options } = slideData;
  const slideName = slide?.name ?? "";

  const update = (patch: Partial<PhonicsEncodeSlideData>) => {
    if (!slide) return;
    dispatch(updateSlide({ slideId: slide.id, slideData: { ...slideData, ...patch } }));
  };

  const handleRemoveAudio = () => {
    update({ audio: EMPTY_AUDIO });
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-attach listeners when audio source changes
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setCurrentTime(el.currentTime);
    const onMeta = () => setDuration(el.duration);
    const onEnd = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("ended", onEnd);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("ended", onEnd);
    };
  }, [audio.src]);

  const formatTime = (t: number) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const handleOptionChange = (index: number, value: string) => {
    const next = [...options];
    next[index] = value;
    update({ options: next });
  };

  const handleAddOption = () => {
    update({ options: [...options, ""] });
  };

  const handleRemoveOption = (index: number) => {
    update({ options: options.filter((_, i) => i !== index) });
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <EditorContainer>
      {/* Header */}
      <div className="flex items-center justify-between shrink-0 px-3">
        <h2 className="text-sm font-semibold text-primary-900 truncate uppercase tracking-widest">
          {slideName || "Sound to Spelling"}
        </h2>
        <span className="text-[10px] font-medium text-gray-100 bg-primary-900 px-2 py-0.5 rounded-full shrink-0">
          Sound to Spelling
        </span>
      </div>

      <div className="flex-1 overflow-y-auto flex flex-col gap-3">
        {/* Sound — one phonics audio and the phoneme it spells (picking audio auto-fills the phoneme) */}
        <div className="bg-surface rounded-lg border border-border overflow-hidden">
          <div className="px-3 py-1.5 bg-muted/40 border-b border-border">
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">
              Sound
            </span>
          </div>

          {audio.src && (
            // biome-ignore lint/a11y/useMediaCaption: phonics audio is pronunciation teaching
            <audio ref={audioRef} src={audio.src} preload="metadata" />
          )}

          <div className="p-3 space-y-3">
            <div className="min-h-[64px] flex flex-col justify-center gap-2">
              {audio.src ? (
                <>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handlePlayPause}
                      className="shrink-0 w-8 h-8 rounded-full bg-blue-500 hover:bg-blue-600 flex items-center justify-center transition-all hover:scale-105 active:scale-95"
                    >
                      {isPlaying ? (
                        <FaPause className="text-white text-[10px]" />
                      ) : (
                        <FaPlay className="text-white text-[10px] ml-0.5" />
                      )}
                    </button>
                    <p className="flex-1 text-xs font-medium text-foreground truncate">
                      {audio.filename || "Phonics audio"}
                    </p>
                    <IconButton variant="danger" onClick={handleRemoveAudio}>
                      <HiX className="text-xs" />
                    </IconButton>
                  </div>
                  <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full transition-all duration-100"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-[9px] text-muted-foreground">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </>
              ) : (
                <p className="text-xs font-medium text-muted-foreground text-center">
                  No audio selected
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={() => setActivePanel("Phonics")}
              className="w-full py-1.5 text-xs font-semibold text-white bg-blue-500 hover:bg-blue-600 rounded-md transition-colors"
            >
              {audio.src ? "Change sound" : "Browse Phonics Library"}
            </button>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground block">Phoneme</label>
              <input
                type="text"
                value={phoneme ?? ""}
                onChange={(e) => update({ phoneme: e.target.value || null })}
                placeholder='e.g. "th", "ai"'
                className="w-full px-3 py-1.5 bg-surface border border-border rounded text-sm font-medium focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none"
              />
            </div>
          </div>
        </div>

        {/* Question — mode, answer and distractors are one unit */}
        <div className="bg-surface rounded-lg border border-border overflow-hidden">
          <div className="px-3 py-1.5 bg-muted/40 border-b border-border">
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">
              Question
            </span>
          </div>

          <div className="p-3 space-y-2">
            <label className="text-xs font-semibold text-muted-foreground block">Mode</label>
            <div className="flex rounded-md border border-border overflow-hidden">
              {(["multiple_choice", "fill_in_blank"] as const).map((mode, i) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => update({ questionMode: mode })}
                  className={`flex-1 py-1.5 px-2 text-xs font-semibold transition-colors ${
                    i > 0 ? "border-l border-border" : ""
                  } ${
                    questionMode === mode
                      ? "bg-blue-500 text-white"
                      : "bg-surface text-foreground hover:bg-muted/50"
                  }`}
                >
                  {mode === "multiple_choice" ? "Multiple Choice" : "Fill in Blank"}
                </button>
              ))}
            </div>
          </div>

          <div className="p-3 pt-0 space-y-2">
            <label className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" />
              Correct Answer
            </label>
            <input
              type="text"
              value={correctAnswer ?? ""}
              onChange={(e) => update({ correctAnswer: e.target.value || null })}
              placeholder="Enter the correct answer..."
              className="w-full px-3 py-1.5 bg-surface border border-border rounded text-sm font-medium focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none"
            />
          </div>

          {questionMode === "multiple_choice" && (
            <div className="p-3 pt-0 space-y-2">
              <label className="text-xs font-semibold text-muted-foreground block">
                Distractors (wrong answers)
              </label>
              {options.map((opt, i) => (
                <div key={`opt-${i + 1}`} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={opt}
                    onChange={(e) => handleOptionChange(i, e.target.value)}
                    placeholder={`Distractor ${i + 1}`}
                    className="flex-1 px-3 py-1.5 bg-surface border border-border rounded text-sm font-medium focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none"
                  />
                  <IconButton variant="danger" onClick={() => handleRemoveOption(i)}>
                    <HiX className="text-xs" />
                  </IconButton>
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddOption}
                className="w-full py-1.5 text-xs font-semibold text-blue-500 border border-dashed border-blue-300 rounded hover:bg-blue-50 transition-colors"
              >
                + Add Distractor
              </button>
            </div>
          )}
        </div>
      </div>
    </EditorContainer>
  );
}
